import { useEffect } from 'react';
import { Box, Divider } from '@mui/material';
import { useField } from 'formik';

import useApi from 'Lib/hooks/useApi';
import debounce from 'Lib/utilities/debounce';

import MentionExtension from '../mention/extension';
import SnippetExtension from '../snippet/extension';
import Wizzy from '..';

const Notes = ({ apis }) => {
	const [field, , helpers] = useField('note');

	// I think all of this fetching ideally should live inside of the extension so it can be resused
	// outside of the context of the notes component. It's currently relient on the `apis` object and
	// will likely move that out when the next use case pops up.
	const getUsers = useApi(apis.getUsers);
	const getSnippets = useApi(apis.getSnippets);

	useEffect(() => {}, []);

	// TODO: Spaces are not working when sent to backend because it's %20. Ask Jon about this
	const debouncedFetchMentions = debounce(async (query, callback) => {
		try {
			const { data } = await getUsers({ search: query });

			callback(
				data.map(({ user }) => ({
					id: user.userID,
					label: `${user.firstName} ${user.lastName}`,
					meta: user
				}))
			);
		} catch {
			callback([]);
		}
	}, 300);

	const debouncedFetchSnippets = debounce(async (query, callback) => {
		try {
			const { data } = await getSnippets({ search: query });

			callback(
				data.map(({ snippet }) => ({
					id: snippet.snippetID,
					label: snippet.name,
					meta: snippet
				}))
			);
		} catch {
			callback([]);
		}
	}, 300);

	const fetchMentions = async query => {
		if (query.length < 1) {
			return [];
		}

		return new Promise(resolve => {
			debouncedFetchMentions(query, results => {
				resolve(results);
			});
		});
	};

	const fetchSnippets = async query => {
		if (query.length < 1) {
			return [];
		}

		return new Promise(resolve => {
			debouncedFetchSnippets(query, results => {
				resolve(results);
			});
		});
	};

	return (
		<Box
			sx={{
				mb: 2,
				border: '1px solid #d1d5db',
				borderRadius: 2,
				backgroundColor: '#ffffff',
				'&:focus-within': {
					borderColor: 'success.main',
					borderWidth: '2px'
					// boxShadow: theme => `0 0 0 1px ${theme.palette.success.main}`
				},
				transition: 'border-color 0.15s ease-in-out, box-shadow 0.15s ease-in-out'
			}}>
			<Wizzy.Wrapper
				placeholder="Enter note"
				defaultContent={field.value}
				extensions={[MentionExtension({ fetchMentions }), SnippetExtension({ fetchSnippets })]}
				onUpdate={({ editor }) => {
					const content = editor.getHTML();
					helpers.setValue(content);
				}}>
				<Box
					sx={{
						borderBottom: '1px solid #e5e7eb',
						px: 2,
						py: 1,
						backgroundColor: '#f9fafb',
						borderRadius: '8px 8px 0 0'
					}}>
					<Wizzy.Toolbar aria-label="text formatting">
						<Wizzy.ToggleAction type="bold" />
						<Wizzy.ToggleAction type="italic" />
						<Wizzy.ToggleAction type="underline" />
						<Wizzy.ToggleAction type="strike" />
						<Divider orientation="vertical" flexItem sx={{ m: 2 }} />
						<Wizzy.ToggleAction type="orderedList" />
						<Wizzy.ToggleAction type="bulletList" />
						<Wizzy.ToggleAction type="link" />
						{/* <Wizzy.ToggleAction type="image" /> */}
						<Wizzy.ToggleAction type="horizontalRule" />
					</Wizzy.Toolbar>
				</Box>

				<Box
					sx={{
						p: 2,
						minHeight: 80,
						cursor: 'text',
						'& .ProseMirror': {
							minHeight: 80,
							outline: 'none',
							cursor: 'text'
						}
					}}
					onClick={e => {
						// Focus the editor when clicking anywhere in the box
						const editor = e.currentTarget.querySelector('.ProseMirror');

						if (editor) {
							editor.focus();
						}
					}}>
					<Wizzy.Output />
				</Box>
			</Wizzy.Wrapper>
		</Box>
	);
};

export default Notes;
